import { Box, chakra, Flex } from '@chakra-ui/react';
import { patientCaseContentSchema } from '@llx/common';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AccessLinkTypes, AdminCaseListViewQuery } from '@/@generated/codegen/API';
import { AccessLinkPanelClient } from '@/components/AccessLinkPanel/AccessLinkPanelClient';
import { usePaginationState } from '@/hooks/usePaginationState';
import { useTableQueryParamsUtils } from '@/hooks/useTableQueryParamsUtils';
import { ROUTES } from '@/routes/Routes';
import ColumnVisibility from '@/ui/TanstackTable/Columns/ColumnVisibility';
import ActiveFilters from '@/ui/TanstackTable/Filter/ActiveFilters/ActiveFilters';
import Filters, { FilterExtraInfo } from '@/ui/TanstackTable/Filter/Filters';
import GlobalFilter from '@/ui/TanstackTable/Filter/GlobalFilter/GlobalFilter';
import { FilterType } from '@/ui/TanstackTable/Filter/NewFilterComponent';
import Pagination from '@/ui/TanstackTable/Pagination';
import TanstackTableView from '@/ui/TanstackTable/TanstackTableView';
import { fuzzyFilter } from '@/utils/helpers/fuzzyFilter';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import useAdminCaseListTableStore from './AdminCaseListTableStore';
import getColumns, { AdminCaseListRowType } from './columns';

type Props = {
  data: AdminCaseListViewQuery;
  pagination: ReturnType<typeof usePaginationState>;
  tablePrefix: string;
};

const filterExtraInfo: FilterExtraInfo<AdminCaseListRowType> = [
  {
    filterId: 'creationDate',
    filterType: FilterType.DATE,
  },
  {
    filterId: 'caseClusters',
    filterType: FilterType.STRING,
    queryParamKey: 'caseClusterName',
  },
  {
    filterId: 'client',
    filterType: FilterType.STRING,
    queryParamKey: 'clientName',
  },
];

const AdminCaseListView = ({ data, pagination, tablePrefix }: Props) => {
  const { t } = useTranslation();
  const totalCount = data.listPatientCases.totalCount;
  const navigate = useNavigate();
  const [managedPatientCaseId, setManagedPatientCaseId] = useState<string | undefined>(undefined);
  const [showAccessLinkPanel, setShowAccessLinkPanel] = useState<boolean>(false);

  const handleViewCase = (patientCase: AdminCaseListRowType) => {
    navigate(`${ROUTES.PREVIEW_CASE}/${patientCase.id}`);
  };

  const columns = getColumns({
    t,
    onViewCaseClick: (elem) => {
      handleViewCase(elem);
    },
    onPreviewLinkClick: (elem) => {
      setManagedPatientCaseId(elem.id);
      setShowAccessLinkPanel(true);
    },
  });

  const {
    columnFilters,
    sorting,
    globalFilter,
    onColumnFiltersChange,
    onSortingChange,
    onGlobalFilterChange,
  } = useTableQueryParamsUtils(filterExtraInfo, tablePrefix);
  const [columnVisibility, setColumnVisibility, columnSizing, setColumnSizing] =
    useAdminCaseListTableStore((state) => [
      state.columnVisibility,
      state.setColumnVisibility,
      state.columnSizing,
      state.setColumnSizing,
    ]);
  const tableData = useMemo(() => {
    return data.listPatientCases.edges.map((elem) => {
      try {
        const patientCaseContent = patientCaseContentSchema.parse(
          JSON.parse(elem.node.stringifiedContent)
        );
        const numberOfSlides = patientCaseContent.slides.length;

        return {
          ...elem.node,
          numberOfSlides,
          patientCaseContent,
        };
      } catch (error) {
        captureSentry({
          title: 'Error parsing patient case content',
          error: error,
          detail: {
            elem: elem.node,
          },
        });
        throw error;
      }
    });
  }, [data.listPatientCases]);

  const table = useReactTable({
    data: tableData,
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pagination.paginationState.pageSize),
    state: {
      pagination: pagination.paginationState,
      columnSizing,
      columnVisibility,
      sorting,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(newVisibility);
    },
    onColumnSizingChange: (updater) => {
      const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
      setColumnSizing(newSizing);
    },
    onPaginationChange: pagination.createOnPaginationChangeFunc(data.listPatientCases.pageInfo),
    onGlobalFilterChange: onGlobalFilterChange,
    onSortingChange: onSortingChange,
    defaultColumn: {},
    columnResizeMode: 'onEnd',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columns: columns,
  });

  return (
    <Box p={{ base: 3, md: 3 }}>
      {showAccessLinkPanel && managedPatientCaseId && (
        <AccessLinkPanelClient
          type={AccessLinkTypes.CasePreview}
          objId={managedPatientCaseId}
          onClose={() => {
            setManagedPatientCaseId(undefined);
            setShowAccessLinkPanel(false);
          }}
        />
      )}
      <Flex justifyContent="space-between" wrap="wrap" gap={2} alignItems="center" my={2}>
        <chakra.h3 fontSize="2xl" fontWeight="bold">
          {t('admin.cases.list.title')}
        </chakra.h3>
      </Flex>
      <Flex wrap="wrap" gap={2} alignItems="center" mb="4">
        <ColumnVisibility table={table} />
        <Filters
          columnFilters={columnFilters}
          setColumnFilters={onColumnFiltersChange}
          table={table}
          filterExtraInfo={filterExtraInfo}
        />
        <GlobalFilter
          globalFilter={globalFilter}
          setGlobalFilter={onGlobalFilterChange}
          maxWidth={{ base: 'none', md: '250px' }}
          flex={{ base: 1, md: 'initial' }}
          minW="150px"
        />
        <ActiveFilters
          columnFilters={columnFilters}
          removeItemFunc={(elem) => {
            onColumnFiltersChange(columnFilters.filter((f) => f.id !== elem.id));
          }}
          filterExtraInfo={filterExtraInfo}
        />
        <Pagination
          table={table}
          pageInfo={data.listPatientCases.pageInfo}
          totalRows={totalCount}
          tablePrefix={tablePrefix}
          ml="auto"
        />
      </Flex>
      <TanstackTableView table={table} />
    </Box>
  );
};

export default AdminCaseListView;
